import { useState, useRef, useEffect } from 'react';
import { Plus, X, Camera, Send, Video, Instagram, Download } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Horizontal story avatars row ──
const StoriesRow = ({ storyGroups, onViewStory, onCreateStory }) => {
  const { user } = useAuth();

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none px-1" data-testid="stories-row">
      {/* Create story button */}
      {user && (
        <button onClick={onCreateStory} className="flex flex-col items-center gap-1 flex-shrink-0" data-testid="create-story-btn">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center border-2 border-dashed border-[#FF6B35]/50 hover:border-[#FF6B35] transition-colors">
            <Plus className="w-6 h-6 text-[#FF6B35]" />
          </div>
          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">Your Recipe</span>
        </button>
      )}

      {/* Story avatars */}
      {storyGroups.map((group) => (
        <button
          key={group.user_id}
          onClick={() => onViewStory(group)}
          className="flex flex-col items-center gap-1 flex-shrink-0"
          data-testid={`story-avatar-${group.user_id}`}
        >
          <div className={`p-[2.5px] rounded-full ${group.has_unviewed
            ? 'bg-gradient-to-br from-[#F4C430] via-[#FF8C42] to-[#FF6B35]'
            : 'bg-gray-300 dark:bg-gray-600'}`}>
            <Avatar className="w-[58px] h-[58px] border-2 border-white dark:border-gray-900">
              <AvatarImage src={group.user_avatar} />
              <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-sm font-bold">
                {group.user_name?.[0]?.toUpperCase() || 'F'}
              </AvatarFallback>
            </Avatar>
          </div>
          <span className="text-[10px] text-gray-600 dark:text-gray-400 font-medium max-w-[64px] truncate">
            {group.user_id === user?.user_id ? 'You' : group.user_name?.split(' ')[0]}
          </span>
        </button>
      ))}
    </div>
  );
};

// ── Create Recipe Modal (renamed from Story) — supports video + Instagram share ──
const CreateStoryModal = ({ onClose, onCreated }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posted, setPosted] = useState(false);
  const [postedUrl, setPostedUrl] = useState(null);
  const fileRef = useRef(null);
  const { toast } = useToast();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setIsVideo(f.type.startsWith('video/'));
  };

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('caption', caption);
      const token = localStorage.getItem('fq_auth_token');
      const res = await fetch(`${API_URL}/api/stories`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('Failed to create');
      const story = await res.json();
      onCreated(story);
      setPosted(true);
      setPostedUrl(story.image_url || preview);
      toast({ title: 'Recipe shared!' });
    } catch {
      toast({ title: 'Failed to share recipe', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleShareInstagram = () => {
    // Instagram share via deep link (opens IG app with content)
    const text = encodeURIComponent(`${caption}\n\n#FoodQuest #Recipe #FoodieLife`);
    // Instagram Stories share (works on mobile)
    if (navigator.share) {
      navigator.share({
        title: 'My FoodQuest Recipe',
        text: `${caption}\n\n#FoodQuest #Recipe`,
        url: postedUrl || window.location.href,
      }).catch(() => {});
    } else {
      // Fallback: open Instagram web
      window.open(`https://www.instagram.com/`, '_blank');
    }
  };

  const handleDownload = () => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview;
    a.download = `foodquest-recipe.${isVideo ? 'mp4' : 'jpg'}`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="create-story-modal">
      <div className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <h3 className="font-bold text-gray-900 dark:text-white">{posted ? 'Recipe Posted!' : 'New Recipe'}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {posted ? (
          /* Success — show share options */
          <div className="p-5 space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Send className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-sm text-gray-600">Your recipe is live in the feed!</p>
            </div>
            <button onClick={handleShareInstagram} className="w-full h-11 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 hover:opacity-90 transition-all" data-testid="share-instagram-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              Share to Instagram
            </button>
            <button onClick={handleDownload} className="w-full h-10 bg-gray-100 text-gray-700 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors" data-testid="download-recipe-btn">
              <Download className="w-4 h-4" />
              Download for Upload
            </button>
            <button onClick={onClose} className="w-full text-sm text-gray-400 hover:text-gray-600 py-2">Done</button>
          </div>
        ) : (
          /* Upload flow */
          <>
            {preview ? (
              <div className="relative aspect-[9/16] max-h-[55vh] bg-black">
                {isVideo ? (
                  <video src={preview} controls playsInline className="w-full h-full object-contain" />
                ) : (
                  <img src={preview} alt="Preview" className="w-full h-full object-contain" />
                )}
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()}
                className="w-full aspect-[9/16] max-h-[55vh] bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center gap-1.5">
                    <Camera className="w-10 h-10 text-[#FF6B35]" />
                    <span className="text-xs text-gray-400">Photo</span>
                  </div>
                  <div className="w-px h-12 bg-gray-200" />
                  <div className="flex flex-col items-center gap-1.5">
                    <Video className="w-10 h-10 text-[#FF6B35]" />
                    <span className="text-xs text-gray-400">Video</span>
                  </div>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 mt-2">Tap to add photo or video (9:16)</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFile} />

            <div className="p-4 space-y-3">
              <input
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="Add a caption... #recipe #foodie"
                className="w-full text-sm bg-gray-50 dark:bg-gray-800 dark:text-white rounded-xl px-3 py-2.5 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
              />
              <button
                onClick={handleSubmit}
                disabled={!file || uploading}
                data-testid="submit-story-btn"
                className="w-full h-10 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-90 transition-all"
              >
                {uploading ? 'Sharing...' : 'Share Recipe'}
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Floating Reaction Animation ──
const FloatingReaction = ({ emoji, onDone }) => {
  useEffect(() => {
    const timer = setTimeout(onDone, 1800);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  const left = 20 + Math.random() * 60;
  return (
    <div
      className="absolute z-30 text-3xl pointer-events-none"
      style={{
        left: `${left}%`,
        bottom: '120px',
        animation: 'floatUp 1.8s ease-out forwards',
      }}
    >
      {emoji}
    </div>
  );
};

// ── Full-screen Story Viewer ──
const StoryViewer = ({ group, onClose }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [showStars, setShowStars] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagQuery, setTagQuery] = useState('');
  const [tagResults, setTagResults] = useState([]);
  const timerRef = useRef(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const stories = group.stories || [];
  const story = stories[currentIdx];

  const DURATION = 5000;

  const pauseTimer = () => clearInterval(timerRef.current);
  const resumeTimer = () => {
    if (!story) return;
    const remaining = DURATION * (1 - progress / 100);
    const startTime = Date.now();
    const startPct = progress;
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(startPct + (elapsed / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        if (currentIdx < stories.length - 1) setCurrentIdx(prev => prev + 1);
        else onClose();
      }
    }, 50);
  };

  useEffect(() => {
    if (!story) return;
    fetch(`${API_URL}/api/stories/${story.story_id}/view`, { method: 'POST', credentials: 'include' }).catch(() => {});

    setProgress(0);
    setShowStars(false);
    setShowTagInput(false);
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min((elapsed / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        if (currentIdx < stories.length - 1) setCurrentIdx(prev => prev + 1);
        else onClose();
      }
    }, 50);

    return () => clearInterval(timerRef.current);
  }, [currentIdx, story?.story_id]); // eslint-disable-line

  const addFloating = (emoji) => {
    const id = Date.now() + Math.random();
    setFloatingReactions(prev => [...prev, { id, emoji }]);
  };

  const sendReaction = async (type, value, emoji) => {
    if (!user) { toast({ title: 'Sign in to react', variant: 'destructive' }); return; }
    addFloating(emoji);
    try {
      await fetch(`${API_URL}/api/stories/${story.story_id}/react`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, value }),
      });
    } catch {}
  };

  const handleFoodlist = () => {
    sendReaction('foodlist', '', '\uD83D\uDCBE');
    toast({ title: 'Saved to Foodlist!' });
  };

  const handleStar = (rating) => {
    sendReaction('star', String(rating), '\u2B50');
    setShowStars(false);
    toast({ title: `Rated ${rating} stars!` });
  };

  const handleRecipe = () => {
    sendReaction('recipe', '', '\uD83D\uDCD6');
    toast({ title: 'Saved to Recipes!' });
  };

  const handleTagSearch = async (q) => {
    setTagQuery(q);
    if (q.length < 2) { setTagResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/social/discover`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const matches = (d.suggestions || []).filter(s =>
          s.name?.toLowerCase().includes(q.toLowerCase())
        ).slice(0, 5);
        setTagResults(matches);
      }
    } catch {}
  };

  const handleTag = async (targetUser) => {
    if (!user) return;
    addFloating('\uD83C\uDFF7\uFE0F');
    try {
      await fetch(`${API_URL}/api/stories/${story.story_id}/tag`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: targetUser.user_id }),
      });
      toast({ title: `Tagged ${targetUser.name}!` });
    } catch {}
    setShowTagInput(false);
    setTagQuery('');
    setTagResults([]);
  };

  const goNext = () => {
    clearInterval(timerRef.current);
    if (currentIdx < stories.length - 1) setCurrentIdx(prev => prev + 1);
    else onClose();
  };

  const goPrev = () => {
    clearInterval(timerRef.current);
    if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
    else setProgress(0);
  };

  if (!story) return null;

  const timeAgo = (() => {
    const diff = Date.now() - new Date(story.created_at).getTime();
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return `${Math.floor(diff / 60000)}m ago`;
    return `${hrs}h ago`;
  })();

  return (
    <div className="fixed inset-0 z-[70] bg-black flex items-center justify-center" data-testid="story-viewer">
      {/* Float animation keyframe */}
      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          50% { opacity: 0.9; transform: translateY(-120px) scale(1.3); }
          100% { opacity: 0; transform: translateY(-260px) scale(0.8); }
        }
      `}</style>

      {/* Floating reactions */}
      {floatingReactions.map(r => (
        <FloatingReaction
          key={r.id}
          emoji={r.emoji}
          onDone={() => setFloatingReactions(prev => prev.filter(x => x.id !== r.id))}
        />
      ))}

      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 px-3 pt-3">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-none"
              style={{ width: i < currentIdx ? '100%' : i === currentIdx ? `${progress}%` : '0%' }}
            />
          </div>
        ))}
      </div>

      {/* User header */}
      <div className="absolute top-8 left-0 right-0 z-10 flex items-center justify-between px-4">
        <div className="flex items-center gap-2.5">
          <Avatar className="w-9 h-9 border-2 border-white/30">
            <AvatarImage src={group.user_avatar} />
            <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs font-bold">
              {group.user_name?.[0]?.toUpperCase() || 'F'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-white text-sm font-semibold">{group.user_name}</p>
            <p className="text-white/50 text-[10px]">{timeAgo}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20" data-testid="close-story-viewer">
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Story image */}
      <img src={story.image_url} alt={story.caption} className="w-full h-full object-contain" />

      {/* Caption */}
      {story.caption && (
        <div className="absolute bottom-28 left-0 right-0 px-6 text-center z-10">
          <p className="text-white text-sm font-medium bg-black/30 backdrop-blur-sm rounded-2xl px-4 py-2 inline-block">
            {story.caption}
          </p>
        </div>
      )}

      {/* ── Interaction Bar ── */}
      <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-6 pt-16 bg-gradient-to-t from-black/80 to-transparent">
        {/* Star rating overlay */}
        {showStars && (
          <div className="flex justify-center gap-3 mb-3 bg-black/50 backdrop-blur-sm rounded-2xl py-3 mx-auto w-fit px-5" data-testid="star-rating-panel">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => handleStar(s)} data-testid={`story-star-${s}`}
                className="text-3xl hover:scale-125 active:scale-90 transition-transform">
                {'\u2B50'}
              </button>
            ))}
          </div>
        )}

        {/* Tag input overlay */}
        {showTagInput && (
          <div className="mb-3 bg-black/60 backdrop-blur-sm rounded-2xl p-3 mx-4" data-testid="tag-input-panel">
            <input
              value={tagQuery}
              onChange={e => handleTagSearch(e.target.value)}
              placeholder="Search people to tag..."
              autoFocus
              className="w-full text-sm bg-white/10 text-white rounded-xl px-3 py-2 border border-white/20 focus:outline-none focus:ring-1 focus:ring-[#FF6B35] placeholder:text-white/40"
            />
            {tagResults.length > 0 && (
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {tagResults.map(u => (
                  <button key={u.user_id} onClick={() => handleTag(u)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/10 transition-colors text-left">
                    <Avatar className="w-7 h-7">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="bg-[#FF6B35] text-white text-[10px]">{u.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="text-white text-sm">{u.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-3">
          <button onClick={handleFoodlist} data-testid="story-foodlist-btn"
            className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 transition-all"
            onMouseEnter={pauseTimer} onMouseLeave={resumeTimer}>
            <span className="text-xl">{'\uD83D\uDCBE'}</span>
            <span className="text-[10px] text-white/70 font-medium">Foodlist</span>
          </button>

          <button onClick={() => { setShowStars(s => !s); setShowTagInput(false); }} data-testid="story-star-btn"
            className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 transition-all"
            onMouseEnter={pauseTimer} onMouseLeave={resumeTimer}>
            <span className="text-xl">{'\u2B50'}</span>
            <span className="text-[10px] text-white/70 font-medium">Rate</span>
          </button>

          <button onClick={handleRecipe} data-testid="story-recipe-btn"
            className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 transition-all"
            onMouseEnter={pauseTimer} onMouseLeave={resumeTimer}>
            <span className="text-xl">{'\uD83D\uDCD6'}</span>
            <span className="text-[10px] text-white/70 font-medium">Recipe</span>
          </button>

          <button onClick={() => { setShowTagInput(t => !t); setShowStars(false); }} data-testid="story-tag-btn"
            className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl bg-white/10 hover:bg-white/20 active:scale-95 transition-all"
            onMouseEnter={pauseTimer} onMouseLeave={resumeTimer}>
            <span className="text-xl">{'\uD83C\uDFF7\uFE0F'}</span>
            <span className="text-[10px] text-white/70 font-medium">Tag</span>
          </button>
        </div>
      </div>

      {/* Tap zones (adjusted for interaction bar) */}
      <button onClick={goPrev} className="absolute left-0 top-16 bottom-28 w-1/3 z-10" aria-label="Previous" />
      <button onClick={goNext} className="absolute right-0 top-16 bottom-28 w-1/3 z-10" aria-label="Next" />
    </div>
  );
};

export { StoriesRow, CreateStoryModal, StoryViewer };
